import { useState, useEffect } from 'react'
import { useAuthStore } from '../../stores/useAuthStore.js'
import { useChatStore } from '../../stores/useChatStore.js'
import { useSocket } from '../../hooks/useSocket.js'
import { apiFetch } from '../../lib/api-client.js'
import { ConversationList } from './ConversationList.js'
import { ConversationPanel } from './ConversationPanel.js'
import { PendingConversationPanel } from './PendingConversationPanel.js'
import { NewConversationSearch } from './NewConversationSearch.js'
import { CreateGroupPanel } from './CreateGroupPanel.js'
import { EmptyState } from '../../components/index.js'
import type { UserProfile } from '../../types/user.js'

type SideView = 'list' | 'new-chat' | 'create-group'

export function AppShell() {
  useSocket()

  const user = useAuthStore((s) => s.user)!
  const activeChat = useChatStore((s) => s.activeChat)
  const setActiveChat = useChatStore((s) => s.setActiveChat)
  const [users, setUsers] = useState<UserProfile[]>([])
  const [sideView, setSideView] = useState<SideView>('list')
  const [pendingUser, setPendingUser] = useState<UserProfile | null>(null)

  useEffect(() => {
    apiFetch<UserProfile[]>('/api/auth/users')
      .then(setUsers)
      .catch((err) => console.error('[ChatInterno] Erro ao carregar usuários:', err))
  }, [])

  function handleSelectUser(targetUser: UserProfile) {
    setPendingUser(targetUser)
    setSideView('list')
    setActiveChat(null)
  }

  function handleChatCreated(chatId: string) {
    setPendingUser(null)
    setSideView('list')
    setActiveChat(chatId)
  }

  function handleSelectExistingChat(chatId: string) {
    setPendingUser(null)
    setSideView('list')
    setActiveChat(chatId)
  }

  return (
    <div className="flex h-screen bg-(--color-bg-primary)">
      {/* Conversation list — fixed width */}
      <div className="w-80 shrink-0">
        {sideView === 'list' && (
          <ConversationList
            users={users}
            onNewChat={() => { setSideView('new-chat'); setPendingUser(null) }}
            onCreateGroup={() => { setSideView('create-group'); setPendingUser(null) }}
            onSelectChat={handleSelectExistingChat}
          />
        )}
        {sideView === 'new-chat' && (
          <NewConversationSearch
            users={users}
            currentUser={user}
            onSelectUser={handleSelectUser}
            onClose={() => setSideView('list')}
          />
        )}
        {sideView === 'create-group' && (
          <CreateGroupPanel
            users={users}
            currentUser={user}
            onCreated={handleChatCreated}
            onClose={() => setSideView('list')}
          />
        )}
      </div>

      {/* Main panel */}
      <div className="flex flex-1 flex-col">
        {pendingUser ? (
          <PendingConversationPanel
            targetUser={pendingUser}
            currentUser={user}
            users={users}
            onChatCreated={handleChatCreated}
          />
        ) : activeChat ? (
          <ConversationPanel chatId={activeChat} users={users} currentUser={user} />
        ) : (
          <EmptyState
            title="Selecione uma conversa"
            description="Ou inicie uma nova conversa com um colega"
          />
        )}
      </div>
    </div>
  )
}
